import { useDebounce } from 'use-debounce'
import { Button } from '../../components/UI/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/UI/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/UI/popover'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { clientInstance } from 'utils/http-client'
import { useQuery } from '@tanstack/react-query'
import { cn } from 'components/UI/cn'
import { DoorOption } from './importer-types'

export function useDoorSearch(query: string) {
  return useQuery<DoorOption[]>({
    queryKey: ['door-search', query],
    queryFn: async () => {
      const url = `importengine/doors/search/?q=${encodeURIComponent(query)}`
      const client = clientInstance
      const response = await client.get(url)
      return response.data
    },
  })
}

interface DoorAutocompleteProps {
  value?: string
  onChange?: (door: DoorOption) => void
  placeholder?: string
  error?: boolean
  selectedDoor?: DoorOption
}
export function DoorAutocomplete(props: DoorAutocompleteProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounce(search, 300)

  const { data: options = [], isLoading } = useDoorSearch(debouncedSearch)

  // Reset search when closing the popover
  useEffect(() => {
    if (!open) {
      setSearch('')
    }
  }, [open])

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between',
              props.error && 'border-red-500 focus-visible:ring-red-500'
            )}
          >
            {props.selectedDoor ? props.selectedDoor.name : props.placeholder}
            {open && isLoading ? (
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            ) : (
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by chain name or domain..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : options.length === 0 ? (
                <CommandEmpty>No chains found.</CommandEmpty>
              ) : (
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.item_id}
                      value={option.item_id}
                      onSelect={() => props.onChange?.(option)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          props.selectedDoor?.item_id === option.item_id
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{option.name}</span>
                        <span className="text-sm text-muted-foreground">
                          {option.address}
                          {option.city}, {option.state} {option.zipcode}
                          {option.country}
                          {option.domain}
                          {option.chain_name}
                          {option.chain_size}
                          {option.type}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
